import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { relativeTime } from "../lib/notifications";

const STATUS_LABELS = { SENT: "Envoyés", PENDING: "En attente", PROCESSING: "En cours", FAILED: "Échoués" };
const STATUS_ONE = { SENT: "Envoyé", PENDING: "En attente", PROCESSING: "En cours", FAILED: "Échoué" };
const STATUS_TONE = { SENT: "text-emerald-700 bg-emerald-50", PENDING: "text-slate-700 bg-slate-100", PROCESSING: "text-slate-700 bg-slate-100", FAILED: "text-red-700 bg-red-50" };

// Paramètres › Notifications › « Journal d'envoi » (Ordre 24-3): what the WhatsApp queue did over the last days. Counts per event and state
// and the latest sends — never the text of a message and never a phone number. `labelOf(setting key)` gives the event's plain label.
export default function DeliveryJournal({ labelOf }) {
  const [state, setState] = useState({ loading: true, error: false, data: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/deliveries");
      setState({ loading: false, error: false, data });
    } catch (e) {
      setState((prev) => ({ ...prev, loading: false, error: true }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { loading, error, data } = state;
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="delivery-journal">
      <div>
        <h3 className="font-display font-semibold text-sm text-slate-900">Journal d'envoi</h3>
        <p className="text-xs text-slate-500 mt-0.5">Ce que la file WhatsApp a fait ces {data?.days || 7} derniers jours. Le contenu des messages et les numéros n'y figurent jamais.</p>
      </div>
      {loading ? (
        <div className="text-sm text-slate-400" data-testid="journal-loading">Chargement…</div>
      ) : error ? (
        <div className="text-sm text-slate-500" data-testid="journal-error">Journal indisponible. <button type="button" onClick={load} className="text-jamora hover:underline">Réessayer</button></div>
      ) : !data.recent.length ? (
        <div className="text-sm text-slate-500" data-testid="journal-empty">Aucun envoi WhatsApp sur la période. Les alertes de la cloche n'y apparaissent pas.</div>
      ) : (
        <>
          <ul className="flex flex-wrap gap-2" data-testid="journal-totals">
            {Object.keys(STATUS_LABELS).filter((s) => data.totals[s]).map((s) => (
              <li key={s} className={`rounded-full px-3 h-7 inline-flex items-center gap-2 text-xs font-medium ${STATUS_TONE[s]}`}>{STATUS_LABELS[s]}<span className="font-mono">{data.totals[s]}</span></li>
            ))}
          </ul>
          <ul className="divide-y divide-slate-100 sm:hidden" data-testid="journal-by-event-list">
            {data.by_event.map((row) => (
              <li key={row.event} className="py-2.5">
                <div className="text-sm text-slate-800">{labelOf(row.setting) || row.event}</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {Object.keys(STATUS_LABELS).filter((st) => row.counts[st]).map((st) => (
                    <span key={st} className={`rounded-full px-2 h-5 inline-flex items-center gap-1.5 text-2xs font-medium ${STATUS_TONE[st]}`}>{STATUS_LABELS[st]}<span className="font-mono">{row.counts[st]}</span></span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="journal-by-event">
              <thead><tr className="text-left text-xs text-slate-500"><th className="py-2 pr-3 font-semibold">Événement</th>{Object.keys(STATUS_LABELS).map((s) => <th key={s} className="py-2 px-2 font-semibold text-right">{STATUS_LABELS[s]}</th>)}</tr></thead>
              <tbody>
                {data.by_event.map((row) => (
                  <tr key={row.event} className="border-t border-slate-100">
                    <td className="py-2 pr-3 text-slate-800">{labelOf(row.setting) || row.event}</td>
                    {Object.keys(STATUS_LABELS).map((s) => <td key={s} className="py-2 px-2 text-right font-mono text-slate-600">{row.counts[s] || "–"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Derniers envois</div>
            <ul className="divide-y divide-slate-100" data-testid="journal-recent">
              {data.recent.map((r, i) => (
                <li key={i} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className={`rounded-full px-2 h-5 inline-flex items-center text-2xs font-medium ${STATUS_TONE[r.status]}`}>{STATUS_ONE[r.status] || r.status}</span>
                  <span className="text-slate-800">{labelOf(r.setting) || r.event}</span>
                  <span className="text-slate-500">→ {r.to}</span>
                  <span className="text-xs text-slate-400 ml-auto">{relativeTime(r.sent_at || r.created_at)}</span>
                  {r.reason && <span className="basis-full text-xs text-red-700">{r.reason}</span>}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </section>
  );
}
